import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getRepFirms, createRepFirm } from '../api';
import Modal from '../components/Modal';
import SortableHeader from '../components/SortableHeader';
import { ColumnsButton } from '../components/ColumnConfigurator';
import useColumns from '../hooks/useColumns';
import usePagination from '../hooks/usePagination';
import Pagination from '../components/Pagination';
import { TableSkeleton, CardSkeleton } from '../components/TableSkeleton';
import RowActions from '../components/RowActions';
import EmptyValue from '../components/EmptyValue';
import MobileCard from '../components/MobileCard';
import useMediaQuery from '../hooks/useMediaQuery';
import { Plus, Search, Briefcase, Eye, Edit, X } from 'lucide-react';
import EmptyState, { EmptyStateFiltered } from '../components/EmptyState';

function RepFirms() {
  const { canDo } = useAuth();
  const navigate = useNavigate();
  const { visibleColumns } = useColumns('repFirms');
  const isMobile = useMediaQuery('(max-width: 768px)');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({ name: '', website: '', phone: '', address: '', country: '', state: '', notes: '' });
  const [localError, setLocalError] = useState('');

  const {
    data: repFirms, loading, error, page, totalPages, total, limit,
    setPage, setLimit, sortBy, sortOrder, handleSort, search, setSearch, refetch
  } = usePagination(getRepFirms, {
    queryKey: 'rep_firms',
    initialLimit: 25,
    initialSortBy: 'name',
    initialSortOrder: 'asc',
  });

  const displayError = localError || error;

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const response = await createRepFirm(formData);
      setShowCreateModal(false);
      setFormData({ name: '', website: '', phone: '', address: '', country: '', state: '', notes: '' });
      navigate(`/rep-firms/${response.data.id}`);
    } catch (err) {
      setLocalError(err.response?.data?.error || 'Failed to create rep firm');
    }
  };

  const renderCell = (col, rf) => {
    switch (col.key) {
      case 'name':
        return <Link to={`/rep-firms/${rf.id}`}>{rf.name}</Link>;
      case 'website':
        return rf.website ? <a href={rf.website} target="_blank" rel="noopener noreferrer">{rf.website}</a> : <EmptyValue />;
      case 'created_at':
        return rf.created_at ? new Date(rf.created_at).toLocaleDateString() : <EmptyValue />;
      default:
        return rf[col.key] || <EmptyValue />;
    }
  };

  const getRowActions = (rf) => [
    { key: 'view', label: 'View', icon: Eye, onClick: () => navigate(`/rep-firms/${rf.id}`) },
    canDo('rep_firms', 'edit') && { key: 'edit', label: 'Edit', icon: Edit, onClick: () => navigate(`/rep-firms/${rf.id}`) },
  ].filter(Boolean);

  return (
    <div className="rep-firms-page">
      <div className="page-header">
        <h1 className="page-title">Rep Firms</h1>
        <div className="page-actions">
          <ColumnsButton entityType="repFirms" />
          {canDo('rep_firms', 'create') && (
            <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
              <Plus size={18} />
              New Rep Firm
            </button>
          )}
        </div>
      </div>

      {displayError && <div className="alert alert-error" role="alert" aria-live="assertive">{displayError}</div>}

      <div className="card mb-4">
        <div className="card-body">
          <div className="filter-bar">
            <div className="search-input">
              <Search size={18} className="search-input-icon" aria-hidden="true" />
              <input
                type="text"
                className="form-input"
                placeholder="Search rep firms..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search rep firms"
              />
            </div>
            {search && (
              <button
                className="btn-clear-filters"
                onClick={() => setSearch('')}
              >
                <X size={14} />
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {isMobile ? (
        <div className="mobile-card-list">
          {loading ? <CardSkeleton count={4} /> : repFirms.length === 0 ? (
            search ? (
              <EmptyStateFiltered
                onClearFilters={() => setSearch('')}
              />
            ) : (
              <EmptyState
                icon={Briefcase}
                title="No rep firms yet"
                description="Add your first rep firm to manage your sales representative partnerships."
                actionLabel={canDo('rep_firms', 'create') ? 'New Rep Firm' : undefined}
                onAction={canDo('rep_firms', 'create') ? () => setShowCreateModal(true) : undefined}
                actionIcon={Plus}
              />
            )
          ) : repFirms.map(rf => (
            <MobileCard
              key={rf.id}
              columns={visibleColumns}
              data={rf}
              renderCell={renderCell}
              actions={<RowActions actions={getRowActions(rf)} id={rf.id} />}
              primaryField="name"
            />
          ))}
        </div>
      ) : (
        <div className="card">
          <div className="table-container">
            <table className="table" aria-label="Rep firms list">
              <thead>
                <tr>
                  {visibleColumns.map(col => (
                    <SortableHeader key={col.key} label={col.label} field={col.key} sortField={sortBy} sortDirection={sortOrder} onSort={handleSort} />
                  ))}
                  <th scope="col" className="col-actions"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <TableSkeleton columns={visibleColumns.length} rows={8} hasCheckbox={false} hasActions />
                ) : repFirms.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColumns.length + 1}>
                      {search ? (
                        <EmptyStateFiltered
                          onClearFilters={() => setSearch('')}
                        />
                      ) : (
                        <EmptyState
                          icon={Briefcase}
                          title="No rep firms yet"
                          description="Add your first rep firm to manage your sales representative partnerships."
                          actionLabel={canDo('rep_firms', 'create') ? 'New Rep Firm' : undefined}
                          onAction={canDo('rep_firms', 'create') ? () => setShowCreateModal(true) : undefined}
                          actionIcon={Plus}
                        />
                      )}
                    </td>
                  </tr>
                ) : (
                  repFirms.map(rf => (
                    <tr key={rf.id}>
                      {visibleColumns.map(col => (
                        <td key={col.key}>{renderCell(col, rf)}</td>
                      ))}
                      <td className="col-actions">
                        <RowActions actions={getRowActions(rf)} id={rf.id} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && repFirms.length > 0 && (
        <Pagination page={page} totalPages={totalPages} total={total} limit={limit}
          onPageChange={setPage} onLimitChange={setLimit} />
      )}

      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="New Rep Firm">
        <form onSubmit={handleCreate}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Name *</label>
              <input type="text" className="form-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
            </div>
            <div className="grid grid-cols-2">
              <div className="form-group">
                <label className="form-label">Website</label>
                <input type="url" className="form-input" value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input type="tel" className="form-input" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Address</label>
              <input type="text" className="form-input" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
            </div>
            <div className="grid grid-cols-2">
              <div className="form-group">
                <label className="form-label">Country</label>
                <input type="text" className="form-input" value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">State</label>
                <input type="text" className="form-input" value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea className="form-textarea" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary">Create</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default RepFirms;
